import {
  AidRequestForm,
  Association,
  Category,
  DateAndTimeRange,
  GroupOpportunity,
  Image,
  OpportunityAvailability,
  OpportunityCategory,
  OpportunityParticipant,
  OpportunityPopulation,
  OpportunityReport,
  OpportunitySkill,
  Organization,
  OrganizationManager,
  PartnerOrganization,
  Population,
  RepeatedOpportunity,
  Status,
  SubCategory,
  Team,
  TeamOpportunity,
} from "@entities";
import { extender } from "@shared/backend/class-extender";
import HyperEntity from "@shared/backend/hyper-entity.decorator";
import {
  GENDER_LITERAL,
  GENDER_TO_HEB,
  REPEAT_TYPES,
  REQUEST_DELETED_REASON,
  WEEK_DAYS,
} from "@shared/helpers/constants";
import { JsonField } from "@shared/helpers/custom-fields";
import { opportunityOccupiedSeatsSql } from "@shared/helpers/opportunity-occupancy";
import {
  opportunityDailyTargetReachedSql,
  opportunityTodayApprovedSeatsSql,
} from "@shared/helpers/daily-volunteer-target";
import { isAdminsOrCurrentUser } from "@shared/helpers/include-in-api.function";
import {
  OpportunityVolunteerMode,
  type OpportunityVolunteerModeType,
} from "@shared/helpers/opportunity-volunteer-mode";
import {
  VolunteerRegistrationOutcome,
  type VolunteerRegistrationOutcomeType,
} from "@shared/helpers/volunteer-registration-outcome";
import { Roles } from "@shared/Roles";
import { Fields, Relations, Validators, remult } from "remult";
import { sqlRelations } from "remult/internals";
import { Chat } from "../chat/entity";
import { CommunicationRecord } from "../communication-record";
import { OpportunityEntityConfig } from "./config";
import { OpportunityFilters } from "./filters";
import { OpportunityMethods } from "./methods";

export interface RepeatConfig {
  repeatType: (typeof REPEAT_TYPES)[number];
  repeatInterval: number;
  days: (typeof WEEK_DAYS)[number][];
}

export interface RequestType extends Partial<Opportunity> {
  selectedTeams?: Team[];
  selectedCategories?: (Category | { id: string } | string)[];
  selectedPopulations?: (Population | { id: string } | string)[];
  repeatConfig?: RepeatConfig;
  action?: "edit" | "delete" | "duplicate";
  editRepeatedType?: "all" | "one";
  /** Optimistic concurrency — reject save if opportunity changed since load */
  expectedUpdatedAt?: Date | string;
}

export type TimesOfDay = "MORNING" | "AFTERNOON" | "EVENING";

@HyperEntity<Opportunity>("opportunity", OpportunityEntityConfig)
export class Opportunity extends extender(
  OpportunityFilters,
  OpportunityMethods,
) {
  @Fields.id()
  id = "";

  @Fields.string({ caption: "תמונת נושא", dbName: "image_id" })
  imageId?: string;

  @Relations.toOne<Opportunity, Image>(() => Image, {
    field: "imageId",
    dbName: "image_id",
  })
  image?: Image;

  @Fields.string({ dbName: "request_image_id" })
  requestImageId?: string;

  @Relations.toOne<Opportunity, Image>(() => Image, {
    field: "requestImageId",
    dbName: "request_image_id",
  })
  requestImage?: Image;

  @Relations.toMany(() => OpportunityCategory, "opportunityId")
  categories: OpportunityCategory[] = [];

  @Relations.toMany(() => OpportunityPopulation, "opportunityId")
  populations: OpportunityPopulation[] = [];

  @Fields.string({ dbName: "sub_category_id", allowNull: true })
  subCategoryId?: string | null;

  @Relations.toOne<Opportunity, SubCategory>(() => SubCategory, "subCategoryId")
  subCategory?: SubCategory;

  @Fields.string({ dbName: "organization_id" })
  organizationId = "";

  @Relations.toOne<Opportunity, Organization>(
    () => Organization,
    "organizationId",
  )
  organization?: Organization;

  @Fields.string({
    dbName: "partner_organization_id",
    allowNull: true,
    includeInApi: Roles.AdminsAndManager,
  })
  partnerOrganizationId: string | null = null;

  @Relations.toOne<Opportunity, PartnerOrganization>(
    () => PartnerOrganization,
    "partnerOrganizationId",
  )
  partnerOrganization?: PartnerOrganization;

  @Relations.toOne<Opportunity, RepeatedOpportunity>(
    () => RepeatedOpportunity,
    "repeatedOpportunityId",
  )
  repeatedOpportunity?: RepeatedOpportunity;

  @Fields.string({ dbName: "parent_opportunity_id", allowNull: true })
  parentOpportunityId: string | null = null;

  @Relations.toOne<Opportunity, Opportunity>(() => Opportunity, {
    field: "parentOpportunityId",
    dbName: "parent_opportunity_id",
  })
  parentOpportunity?: Opportunity;

  @Relations.toMany<Opportunity, Opportunity>(() => Opportunity, {
    field: "parentOpportunityId",
  })
  childOpportunities: Opportunity[] = [];

  @Fields.boolean({
    dbName: "is_opportunity_group_parent",
    caption: "קבוצת מופעים",
  })
  isOpportunityGroupParent = false;

  @Relations.toMany(() => OpportunityParticipant, "opportunity")
  participantUsers: OpportunityParticipant[] = [];

  @Relations.toMany(() => GroupOpportunity, "opportunityId")
  groups: GroupOpportunity[] = [];

  @Relations.toMany<Opportunity, DateAndTimeRange>(
    () => DateAndTimeRange,
    "opportunityId",
  )
  requesterAvailability: DateAndTimeRange[] = [];

  @Fields.integer<Opportunity>({
    caption: "מספר משתתפים",
    sqlExpression: () =>
      opportunityOccupiedSeatsSql('"opportunity"."id"'),
  })
  participantsCount = 0;

  @Fields.integer<Opportunity>({
    caption: "מספר מתעניינים",
    sqlExpression: () =>
      sqlRelations(Opportunity).participantUsers.$count({
        status: "PENDING",
      }),
  })
  interestedCount = 0;

  @Relations.toMany(() => OpportunitySkill, "opportunityId")
  requiredSkills: OpportunitySkill[] = [];

  @Relations.toMany(() => TeamOpportunity, {
    caption: "קבוצות",
  })
  teams: TeamOpportunity[] = [];

  @Fields.string({ dbName: "manager_id" })
  managerId = "";

  @Relations.toOne<Opportunity, OrganizationManager>(
    () => OrganizationManager,
    "managerId",
  )
  manager?: OrganizationManager;

  @Fields.string({ dbName: "handling_manager_id", allowNull: true })
  handlingManagerId?: string;

  @Relations.toOne<Opportunity, OrganizationManager>(
    () => OrganizationManager,
    "handlingManagerId",
  )
  handlingManager?: OrganizationManager;

  @Fields.date({ dbName: "handling_since", allowNull: true })
  handlingSince?: Date;

  /** 0 when the signed-in manager is the handler, so tables can sort those rows first. */
  @Fields.integer({
    allowApiUpdate: false,
    includeInApi: Roles.AdminsAndManager,
    caption: "בטיפול שלי",
    sqlExpression: () => {
      const id = remult.user?.id ?? "";
      const safe =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          id,
        )
          ? id
          : "00000000-0000-0000-0000-000000000000";
      return `CASE WHEN "opportunity"."handling_manager_id" = '${safe}' THEN 0 ELSE 1 END`;
    },
  })
  handlingOwnFirst = 1;

  @Relations.toOne<Opportunity, Association>(() => Association, {
    fields: {
      id: "associationRequestedId",
    },
    caption: "פרטי המבקש/ת",
  })
  associationRequested?: Association;

  @Fields.string({ dbName: "association_requested_id", allowNull: true })
  associationRequestedId: string | null = null;

  @Relations.toOne<Opportunity, AidRequestForm>(() => AidRequestForm, {
    fields: {
      id: "aidRequestFormId",
    },
  })
  form?: AidRequestForm;

  @Fields.string({ dbName: "aid_request_form_id", allowNull: true })
  aidRequestFormId?: string;

  @Fields.boolean({ dbName: "is_send_association_aid_message" })
  isSendAssociationAidMessage?: boolean = false;

  @Relations.toMany(() => OpportunityReport, "opportunityId")
  reports: OpportunityReport[] = [];

  @Relations.toMany(() => Chat, "opportunityId")
  chats: Chat[] = [];

  @Relations.toMany(() => CommunicationRecord, "opportunityId")
  communicationRecords?: CommunicationRecord[];

  @Fields.string({ validate: Validators.required, caption: "כותרת" })
  title = "";

  @Fields.string({ dbName: "repeated_opportunity_id" })
  repeatedOpportunityId?: string = "";

  @Fields.string({ caption: "מיקום" })
  location = "";

  @JsonField<Opportunity, Opportunity["coordinates"]>({
    allowedKeys: ["lat", "lng"],
  })
  coordinates?: { lat: number; lng: number };

  // PostGIS (geography(Point,4326)) - kept out of API; used for DB-level geo filtering/indexes
  @Fields.string({ dbName: "coordinates_geog", allowNull: true, includeInApi: false })
  coordinatesGeog: any = null;

  @Fields.string({ allowNull: true })
  destination: string | null = null;

  @JsonField<Opportunity, Opportunity["destinationCoordinates"]>({
    allowedKeys: ["lat", "lng"],
    dbName: "destination_coordinates",
  })
  destinationCoordinates?: { lat: number; lng: number };

  @Fields.string({
    dbName: "destination_coordinates_geog",
    allowNull: true,
    includeInApi: false,
  })
  destinationCoordinatesGeog: any = null;

  @JsonField<Opportunity, Opportunity["notificationTemplates"]>({
    allowedKeys: ["participantReminder", "newParticipant"],
    dbName: "notification_templates",
  })
  notificationTemplates?: {
    participantReminder?: string;
    newParticipant?: string;
  };

  @Fields.date({
    dbName: "start_date",
    caption: "תאריך התחלה",
    allowNull: true,
  })
  startDate: Date | null | undefined = new Date();

  @Fields.date({ dbName: "end_date", caption: "תאריך סיום", allowNull: true })
  endDate: Date | null | undefined = new Date();

  @Fields.literal(() => WEEK_DAYS, { allowNull: true })
  day: (typeof WEEK_DAYS)[number] = WEEK_DAYS[new Date().getDay()];

  @Fields.boolean({
    dbName: "is_public",
    caption: "ציבורי",
    valueConverter: { displayValue: (val) => (val ? "כן" : "לא") },
  })
  isPublic = false;

  @Fields.string({ caption: "תיאור" })
  description = "";

  @Fields.boolean({ dbName: "is_send_participant_reminder" })
  isSendParticipantReminder = false;

  @Fields.number<Opportunity>({
    caption: "כמות (דרושה)",
    validate: (opp) => {
      const v = opp.amount;
      if (v != null && (v < 1 || v > 1000))
        throw new Error("כמות מתנדבים חייבת להיות בין 1 ל-1000");
    },
  })
  amount = 1;

  @Fields.number<Opportunity>({
    caption: "מכפיל מאצ'ים",
    dbName: "default_match_weight",
    validate: (opp) => {
      const v = opp.defaultMatchWeight;
      if (v != null && (!Number.isInteger(v) || v < 1 || v > 100))
        throw new Error("מכפיל מאצ'ים חייב להיות בין 1 ל-100");
    },
  })
  defaultMatchWeight = 1;

  @Fields.literal(() => GENDER_LITERAL, {
    caption: "מגדר",
    dbName: "required_gender",
    allowNull: true,
    validate: (_, event) => {
      const value = event.value as string | null | undefined;
      if (value === null || value === undefined || value === "") return true;
      return GENDER_LITERAL.includes(value as any);
    },
    displayValue: (_, value) => {
      const val = value as string | null | undefined;
      if (!val || val === "") return "";
      return GENDER_TO_HEB[val as (typeof GENDER_LITERAL)[number]] || "";
    },
  })
  requiredGender?: (typeof GENDER_LITERAL)[number] | "" | null;

  @JsonField<Opportunity, Opportunity["contactDetails"]>({
    allowedKeys: ["name", "phoneNumber", "position", "profileImageId"],
    dbName: "contact_details",
  })
  contactDetails?: {
    name: string;
    phoneNumber: string;
    position: string;
    profileImageId?: string;
  };

  @Fields.string({ dbName: "time_description", caption: "תיאור זמן" })
  timeDescription = "";

  @Fields.string({
    dbName: "progress_status",
    allowNull: true,
    caption: "סטטוס התקדמות",
  })
  progressStatusId?: string;

  @Relations.toOne<Opportunity, Status>(() => Status, {
    field: "progressStatusId",
    caption: "סטטוס התקדמות",
  })
  progressStatus?: Status;

  @Fields.string({ includeInApi: isAdminsOrCurrentUser, caption: "הערות" })
  comments = "";

  @Fields.number<Opportunity>({
    dbName: "min_age",
    validate: ({ minAge }) => {
      if (minAge < 6) throw new Error("גיל מינימלי צריך להיות לפחות 6");
    },
  })
  minAge = 16;

  @Fields.number<Opportunity>({
    dbName: "max_age",
    validate: ({ maxAge }) => {
      if (maxAge > 120) throw new Error("גיל מקסימלי צריך להיות עד 120");
    },
  })
  maxAge = 100;

  @Fields.boolean({ dbName: "is_closed" })
  isClosed = false;

  @Fields.boolean({
    dbName: "is_repeated",
    caption: "חזרה",
    valueConverter: { displayValue: (val) => (val ? "כן" : "לא") },
  })
  isRepeated = false;

  @Fields.boolean({ dbName: "is_validation_required" })
  isValidationRequired = false;

  @Fields.literal(
    () =>
      [
        OpportunityVolunteerMode.INDIVIDUAL,
        OpportunityVolunteerMode.GROUP,
        OpportunityVolunteerMode.BOTH,
      ] as const,
    {
      dbName: "volunteer_mode",
      caption: "סוג מתנדבים",
      defaultValue: () => OpportunityVolunteerMode.INDIVIDUAL,
    },
  )
  volunteerMode: OpportunityVolunteerModeType =
    OpportunityVolunteerMode.INDIVIDUAL;

  @Fields.boolean({
    dbName: "is_group",
    caption: "Legacy: קבוצה (נגזר מ-volunteerMode)",
  })
  isGroup = false;

  @Fields.boolean({
    dbName: "is_long_term",
    caption: "התנדבות ממושכת (ארוכת טווח)",
  })
  isLongTerm = false;

  @Fields.boolean({ dbName: "is_report_required" })
  isReportRequired = false;

  @Fields.boolean({
    dbName: "has_time",
    defaultValue: () => true,
    caption: "Legacy: יש זמן (מחושב אוטומטית)",
  })
  hasTime = true;

  @Fields.boolean({
    dbName: "has_start_time",
    defaultValue: () => true,
    caption: "Legacy: יש זמן התחלה (מחושב אוטומטית)",
  })
  hasStartTime = true;

  @Fields.boolean({ dbName: "is_full" })
  isFull = false;

  @Fields.boolean({ dbName: "is_participant_reminder" })
  isParticipantReminder = false;

  @Fields.boolean({ dbName: "requires_vehicle" })
  requiresVehicle = false;

  @Fields.boolean({ dbName: "is_sensitive" })
  isSensitive = false;

  @Fields.boolean({ dbName: "is_remote", caption: "מרחוק" })
  isRemote = false;

  @Fields.literal(
    () =>
      [
        VolunteerRegistrationOutcome.REGULAR,
        VolunteerRegistrationOutcome.INTERESTED,
        VolunteerRegistrationOutcome.CHAT,
        VolunteerRegistrationOutcome.SMS_DETAILS,
      ] as const,
    {
      dbName: "volunteer_registration_flow",
      caption: "תהליך הרשמה",
      defaultValue: () => VolunteerRegistrationOutcome.SMS_DETAILS,
    },
  )
  volunteerRegistrationFlow: VolunteerRegistrationOutcomeType =
    VolunteerRegistrationOutcome.SMS_DETAILS;

  @Fields.boolean({ dbName: "is_hidden", caption: "מוסתר ממתנדבים" })
  isHidden = false;

  @Fields.boolean({
    dbName: "call_center_in_volunteer_pool",
    includeInApi: Roles.AdminsAndManager,
    caption: "במאגר מוקד",
  })
  callCenterInVolunteerPool = false;

  @Fields.integer({
    dbName: "call_center_urgency",
    includeInApi: Roles.AdminsAndManager,
    caption: "דחיפות מוקד",
  })
  callCenterUrgency = 0;

  @Fields.integer({
    dbName: "call_center_difficulty",
    includeInApi: Roles.AdminsAndManager,
    caption: "קושי מוקד",
  })
  callCenterDifficulty = 3;

  @Fields.integer({
    dbName: "call_center_max_daily_matches",
    allowNull: true,
    includeInApi: Roles.AdminsAndManager,
    caption: "מקס מאצ'ים ביום למוקד",
  })
  callCenterMaxDailyMatches?: number;

  @Fields.integer({
    dbName: "daily_volunteer_target",
    allowNull: true,
    caption: "יעד מתנדבים יומי",
  })
  dailyVolunteerTarget?: number | null;

  @Fields.integer({
    allowApiUpdate: false,
    caption: "מתנדבים שאושרו היום",
    sqlExpression: () =>
      opportunityTodayApprovedSeatsSql(`"opportunity"."id"`),
  })
  todayApprovedSeats = 0;

  @Fields.boolean({
    allowApiUpdate: false,
    caption: "יעד יומי הושלם",
    sqlExpression: () => opportunityDailyTargetReachedSql(),
  })
  dailyVolunteerTargetReached = false;

  @JsonField<Opportunity, Opportunity["postRegistrationContent"]>({
    allowedKeys: ["text", "links"],
    dbName: "post_registration_content",
    caption: "תוכן בדרואר אחרי הרשמה (טקסט + עד 3 לינקים)",
  })
  postRegistrationContent?: {
    text?: string;
    links?: { label: string; url: string }[];
  };

  @Fields.string({
    dbName: "registration_button_text",
    allowNull: true,
    caption: "טקסט כפתור הרשמה",
    maxLength: 200,
  })
  registrationButtonText?: string;

  @Fields.string({
    dbName: "pre_registration_process_text",
    allowNull: true,
    caption: "טקסט תהליך הרשמה (לפני ההרשמה)",
    maxLength: 3000,
  })
  preRegistrationProcessText?: string;

  @Fields.literal(() => ["APPROVED", "AID_REQUEST", "DECLINED"] as const, {
    allowNull: true,
  })
  status?: "APPROVED" | "AID_REQUEST" | "DECLINED";

  @Fields.date({
    dbName: "request_approved_at",
    allowNull: true,
    caption: "מועד אישור בקשת הסיוע",
  })
  requestApprovedAt?: Date;

  @Fields.date({ dbName: "last_call_to_volunteers" })
  lastCallToVolunteers: Date = new Date();

  @Fields.literal(() => ["HIGH", "CRITICAL", "STANDARD"] as const, {
    dbName: "manager_urgency",
    defaultValue: () => "STANDARD",
  })
  managerUrgency: "HIGH" | "CRITICAL" | "STANDARD" = "STANDARD";

  @Fields.string({ dbName: "whatsapp_group_url" })
  whatsappGroupUrl = "";

  @Relations.toMany<Opportunity, OpportunityAvailability>(
    () => OpportunityAvailability,
    "opportunityId",
  )
  userRequestedAvailabilityTime: OpportunityAvailability[] = [];

  @Fields.boolean({ dbName: "is_deleted" })
  isDeleted = false;

  @Fields.date({ dbName: "deleted_at", allowNull: true })
  deletedAt?: Date;

  @Fields.literal(() => REQUEST_DELETED_REASON, {
    allowNull: true,
    dbName: "deleted_reason",
  })
  deletedReason?: (typeof REQUEST_DELETED_REASON)[number];

  @Fields.string({
    dbName: "target_organization_name",
    caption: "שם ארגון או רשות",
    allowNull: true,
  })
  targetOrganizationName?: string;

  @Fields.createdAt({ caption: "נוצרה בתאריך", dbName: "created_at" })
  createdAt = new Date();

  @Fields.updatedAt({ dbName: "updated_at" })
  updatedAt = new Date();
}
